import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  contactTaskAPI,
  contactLogAPI,
  NoContactCustomer,
  RelationshipUpdate,
} from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

interface QuickContactModalProps {
  customer: NoContactCustomer;
  onClose: () => void;
  onSave: (customerId: number, data: {
    contact_type: string;
    notes: string;
    follow_up_needed: boolean;
    follow_up_date?: string;
  }) => void;
}

const QuickContactModal: React.FC<QuickContactModalProps> = ({ customer, onClose, onSave }) => {
  const [contactType, setContactType] = useState('PHONE');
  const [notes, setNotes] = useState('');
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showRelationship, setShowRelationship] = useState(false);
  const [relSpouse, setRelSpouse] = useState(customer.spouse_name || '');
  const [relChildren, setRelChildren] = useState(customer.children_info || '');
  const [relPets, setRelPets] = useState(customer.pets_info || '');
  const [relHobbies, setRelHobbies] = useState(customer.hobbies_interests || '');
  const [relTalkingPoints, setRelTalkingPoints] = useState(customer.talking_points || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notes.trim()) {
      alert('Please enter contact notes');
      return;
    }
    setSubmitting(true);
    await onSave(customer.customer_id, {
      contact_type: contactType,
      notes,
      follow_up_needed: followUpNeeded,
      follow_up_date: followUpNeeded ? followUpDate : undefined,
    });

    // Non-blocking relationship update
    if (showRelationship) {
      const relData: RelationshipUpdate = {};
      if (relSpouse !== (customer.spouse_name || '')) relData.spouse_name = relSpouse;
      if (relChildren !== (customer.children_info || '')) relData.children_info = relChildren;
      if (relPets !== (customer.pets_info || '')) relData.pets_info = relPets;
      if (relHobbies !== (customer.hobbies_interests || '')) relData.hobbies_interests = relHobbies;
      if (relTalkingPoints !== (customer.talking_points || '')) relData.talking_points = relTalkingPoints;
      if (Object.keys(relData).length > 0) {
        contactTaskAPI.updateCustomerRelationship(customer.customer_id, relData).catch(err => {
          console.error('Failed to update relationship data:', err);
        });
      }
    }

    setSubmitting(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content quick-contact-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Log Contact - {customer.customer_name}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="customer-quick-info">
            <p><strong>Contact:</strong> {customer.contact_person}</p>
            {customer.phone && <p><strong>Phone:</strong> <a href={`tel:${customer.phone}`}>{customer.phone}</a></p>}
            {customer.email && <p><strong>Email:</strong> <a href={`mailto:${customer.email}`}>{customer.email}</a></p>}
            {customer.preferred_contact_method && (
              <p><strong>Preferred:</strong> {customer.preferred_contact_method}{customer.best_time_to_contact ? ` (${customer.best_time_to_contact})` : ''}</p>
            )}
            {customer.talking_points && (
              <div className="talking-points-box">
                <strong>Talking Points:</strong>
                <p>{customer.talking_points}</p>
              </div>
            )}
            {(customer.spouse_name || customer.children_info || customer.pets_info || customer.hobbies_interests || customer.birthday) && (
              <div className="relationship-info-box">
                <strong>Relationship Intel:</strong>
                <div className="relationship-items">
                  {customer.spouse_name && <span className="rel-item">Spouse: {customer.spouse_name}</span>}
                  {customer.birthday && <span className="rel-item">Birthday: {new Date(customer.birthday + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                  {customer.children_info && <span className="rel-item">Kids: {customer.children_info}</span>}
                  {customer.pets_info && <span className="rel-item">Pets: {customer.pets_info}</span>}
                  {customer.hobbies_interests && <span className="rel-item">Interests: {customer.hobbies_interests}</span>}
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Contact Type</label>
              <div className="contact-type-buttons">
                {['PHONE', 'TEXT', 'EMAIL', 'VOICEMAIL', 'IN_PERSON'].map(type => (
                  <button
                    key={type}
                    type="button"
                    className={`contact-type-btn ${contactType === type ? 'active' : ''}`}
                    onClick={() => setContactType(type)}
                  >
                    {type === 'PHONE' && '📞'}
                    {type === 'TEXT' && '💬'}
                    {type === 'EMAIL' && '✉️'}
                    {type === 'VOICEMAIL' && '📱'}
                    {type === 'IN_PERSON' && '🤝'}
                    <span>{type.replace('_', ' ')}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Contact Notes *</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="What was discussed?"
                rows={3}
                required
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={followUpNeeded}
                  onChange={e => setFollowUpNeeded(e.target.checked)}
                />
                Schedule Follow-up
              </label>
            </div>

            {followUpNeeded && (
              <div className="form-group">
                <label>Follow-up Date</label>
                <input
                  type="date"
                  value={followUpDate}
                  onChange={e => setFollowUpDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}

            <div className="relationship-update-section">
              <button
                type="button"
                className="relationship-toggle"
                onClick={() => setShowRelationship(!showRelationship)}
              >
                {showRelationship ? '− Hide Relationship Notes' : '+ Update Relationship Notes'}
              </button>
              {showRelationship && (
                <div className="relationship-fields">
                  <div className="form-row-compact">
                    <div className="form-group">
                      <label>Spouse</label>
                      <input type="text" value={relSpouse} onChange={e => setRelSpouse(e.target.value)} placeholder="Spouse name" />
                    </div>
                    <div className="form-group">
                      <label>Kids</label>
                      <input type="text" value={relChildren} onChange={e => setRelChildren(e.target.value)} placeholder="Children info" />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-group">
                      <label>Pets</label>
                      <input type="text" value={relPets} onChange={e => setRelPets(e.target.value)} placeholder="Pets info" />
                    </div>
                    <div className="form-group">
                      <label>Hobbies / Interests</label>
                      <input type="text" value={relHobbies} onChange={e => setRelHobbies(e.target.value)} placeholder="Hobbies & interests" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Talking Points</label>
                    <textarea value={relTalkingPoints} onChange={e => setRelTalkingPoints(e.target.value)} placeholder="Key talking points for next call" rows={2} />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Log Contact'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const NoContactList: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<NoContactCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<NoContactCustomer | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'critical' | 'warning'>('all');
  const [sortBy, setSortBy] = useState<'days' | 'name' | 'type'>('days');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await contactTaskAPI.getNoContact30();
      setCustomers(response.data.customers);
    } catch (err) {
      console.error('Error loading no-contact customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogContact = async (customerId: number, data: {
    contact_type: string;
    notes: string;
    follow_up_needed: boolean;
    follow_up_date?: string;
  }) => {
    try {
      await contactLogAPI.create({
        customer_id: customerId,
        contact_type: data.contact_type,
        notes: data.notes,
        follow_up_needed: data.follow_up_needed,
        follow_up_date: data.follow_up_date,
      });
      // Remove customer from list after logging contact
      setCustomers(prev => prev.filter(c => c.customer_id !== customerId));
      setSelectedCustomer(null);
    } catch (err) {
      console.error('Error logging contact:', err);
      alert('Failed to log contact');
    }
  };

  const generateMailtoLink = (customer: NoContactCustomer) => {
    const subject = encodeURIComponent(`Checking in - Red Bridge Farm Feed Co.`);
    const body = encodeURIComponent(
      `Hi ${customer.contact_person?.split(' ')[0] || 'there'},\n\n` +
      `I hope you're doing well! I wanted to reach out and see how things are going.\n\n` +
      `Is there anything we can help you with? We'd love to hear from you.\n\n` +
      `Best regards,\n` +
      `Red Bridge Farm Feed Co.`
    );
    return `mailto:${customer.email || ''}?subject=${subject}&body=${body}`;
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return '#dc3545';
      case 'warning': return '#ffc107';
      default: return '#28a745';
    }
  };

  const getUrgencyLabel = (urgency: string) => {
    switch (urgency) {
      case 'critical': return '60+ Days';
      case 'warning': return '45+ Days';
      default: return '30+ Days';
    }
  };

  const filteredCustomers = customers.filter(c => {
    if (filterType === 'all') return true;
    return c.urgency === filterType;
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    switch (sortBy) {
      case 'days':
        return b.days_since_contact - a.days_since_contact;
      case 'name':
        return a.customer_name.localeCompare(b.customer_name);
      case 'type':
        return a.customer_type.localeCompare(b.customer_type);
      default:
        return 0;
    }
  });

  // Stats
  const criticalCount = customers.filter(c => c.urgency === 'critical').length;
  const warningCount = customers.filter(c => c.urgency === 'warning').length;
  const normalCount = customers.filter(c => c.urgency === 'normal').length;

  if (loading) {
    return (
      <div className="crm-page">
        <CRMSubNav />
        <div className="loading">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="crm-page no-contact-page">
      <CRMSubNav />
      <div className="page-header">
        <div className="header-title">
          <h1>30+ Day No Contact</h1>
          <p>Customers who haven't been contacted in 30 or more days</p>
        </div>
        <button className="btn btn-outline" onClick={() => navigate('/crm/monday')}>
          Back to Monday List
        </button>
      </div>

      {/* Stats */}
      <div className="urgency-stats">
        <div
          className={`urgency-stat ${filterType === 'critical' ? 'active' : ''}`}
          onClick={() => setFilterType(filterType === 'critical' ? 'all' : 'critical')}
          style={{ borderColor: '#dc3545' }}
        >
          <div className="stat-value" style={{ color: '#dc3545' }}>{criticalCount}</div>
          <div className="stat-label">60+ Days (Critical)</div>
        </div>
        <div
          className={`urgency-stat ${filterType === 'warning' ? 'active' : ''}`}
          onClick={() => setFilterType(filterType === 'warning' ? 'all' : 'warning')}
          style={{ borderColor: '#ffc107' }}
        >
          <div className="stat-value" style={{ color: '#ffc107' }}>{warningCount}</div>
          <div className="stat-label">45+ Days (Warning)</div>
        </div>
        <div
          className="urgency-stat"
          style={{ borderColor: '#28a745' }}
        >
          <div className="stat-value" style={{ color: '#28a745' }}>{normalCount}</div>
          <div className="stat-label">30+ Days</div>
        </div>
        <div className="urgency-stat total">
          <div className="stat-value">{customers.length}</div>
          <div className="stat-label">Total</div>
        </div>
      </div>

      {/* Filters */}
      <div className="list-controls">
        <div className="sort-control">
          <label>Sort by:</label>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as any)}>
            <option value="days">Days Since Contact</option>
            <option value="name">Customer Name</option>
            <option value="type">Customer Type</option>
          </select>
        </div>
        {filterType !== 'all' && (
          <button className="btn btn-sm btn-outline" onClick={() => setFilterType('all')}>
            Clear Filter
          </button>
        )}
      </div>

      {/* Customer List */}
      {sortedCustomers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎉</div>
          <h3>All caught up!</h3>
          <p>No customers need contact in this category.</p>
        </div>
      ) : (
        <div className="no-contact-list">
          {sortedCustomers.map(customer => (
            <div
              key={customer.customer_id}
              className="no-contact-card"
              style={{ borderLeftColor: getUrgencyColor(customer.urgency) }}
            >
              <div className="customer-main-info">
                <div className="customer-header">
                  <h3>{customer.customer_name}</h3>
                  <div className="customer-badges">
                    <span
                      className="urgency-badge"
                      style={{ backgroundColor: getUrgencyColor(customer.urgency) }}
                    >
                      {getUrgencyLabel(customer.urgency)}
                    </span>
                    <span className="type-badge">{customer.customer_type_display}</span>
                  </div>
                </div>

                <div className="customer-contact-info">
                  {customer.contact_person && (
                    <span className="contact-name">{customer.contact_person}</span>
                  )}
                  {customer.city && (
                    <span className="location">📍 {customer.city}, {customer.state}</span>
                  )}
                </div>

                <div className="last-contact-info">
                  <span className="days-badge">
                    {customer.days_since_contact === 999
                      ? 'Never contacted'
                      : `${customer.days_since_contact} days since contact`}
                  </span>
                  {customer.last_contacted_at && (
                    <span className="last-date">
                      Last: {new Date(customer.last_contacted_at).toLocaleDateString()}
                      {customer.last_contacted_by && ` by ${customer.last_contacted_by}`}
                    </span>
                  )}
                  {customer.last_order_date && (
                    <span className="last-order">
                      Last Order: {new Date(customer.last_order_date).toLocaleDateString()}
                      {customer.last_order_number && ` (#${customer.last_order_number})`}
                    </span>
                  )}
                </div>

                {customer.talking_points && (
                  <div className="talking-points">
                    <strong>Notes:</strong> {customer.talking_points}
                  </div>
                )}

                {(customer.spouse_name || customer.children_info || customer.pets_info || customer.hobbies_interests || customer.birthday) && (
                  <div className="relationship-intel-compact">
                    {customer.spouse_name && <span className="rel-item">Spouse: {customer.spouse_name}</span>}
                    {customer.birthday && <span className="rel-item">Birthday: {new Date(customer.birthday + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                    {customer.children_info && <span className="rel-item">Kids: {customer.children_info}</span>}
                    {customer.pets_info && <span className="rel-item">Pets: {customer.pets_info}</span>}
                    {customer.hobbies_interests && <span className="rel-item">Interests: {customer.hobbies_interests}</span>}
                  </div>
                )}

                {(customer.preferred_contact_method || customer.best_time_to_contact) && (
                  <div className="contact-prefs-compact">
                    {customer.preferred_contact_method && <span>Preferred: {customer.preferred_contact_method}</span>}
                    {customer.best_time_to_contact && <span>Best time: {customer.best_time_to_contact}</span>}
                  </div>
                )}
              </div>

              <div className="customer-actions">
                {customer.phone && (
                  <a href={`tel:${customer.phone}`} className="action-btn phone">
                    📞 {customer.phone}
                  </a>
                )}
                {customer.email && (
                  <a href={generateMailtoLink(customer)} className="action-btn email">
                    ✉️ Email
                  </a>
                )}
                <button
                  className="action-btn log-contact"
                  onClick={() => setSelectedCustomer(customer)}
                >
                  ✓ Log Contact
                </button>
                <button
                  className="action-btn view"
                  onClick={() => navigate(`/crm/customers/${customer.customer_id}`)}
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Contact Modal */}
      {selectedCustomer && (
        <QuickContactModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onSave={handleLogContact}
        />
      )}
    </div>
  );
};

export default NoContactList;
